import type { ApiClient } from './client';

/**
 * Bilt analytics client (MVP proof metrics).
 *
 * Fires proof-metric events to the server-side `analytics_track.php` endpoint.
 * Deliberately fire-and-forget: analytics must never break the UX, so failures
 * are swallowed.
 *
 * Note: some events (currently `vehicle_public_viewed` and `qr_scanned`) are
 * recorded server-side on the endpoints that already serve the public profile,
 * so they do NOT need to be sent from the client.
 */

export type AnalyticsEventName =
  | 'profile_created'
  | 'vehicle_created'
  | 'vehicle_profile_completed'
  | 'vehicle_public_viewed'
  | 'qr_scanned'
  | 'event_created'
  | 'registration_submitted'
  | 'judge_score_submitted'
  | 'judge_score_draft_saved'
  | 'judge_score_unlocked'
  | 'judge_class_started'
  | 'judge_class_completed'
  | 'award_published'
  | 'organizer_export_clicked'
  | 'mod_ai_suggestion_reviewed'
  | 'mod_ai_enhance_applied'
  | 'mod_ai_enhance_undone'
  | 'mod_ai_enhance_completed'
  | 'mod_ai_enhance_abandoned'
  | 'onboarding_started'
  | 'onboarding_step_completed'
  | 'onboarding_step_dismissed'
  | 'onboarding_dismissed'
  | 'onboarding_skipped'
  | 'onboarding_completed'
  | 'onboarding_resumed'
  | 'onboarding_restarted'
  | 'onboarding_first_vehicle_created'
  | 'onboarding_first_modification_added'
  | 'onboarding_first_import_completed'
  | 'onboarding_first_profile_shared'
  | 'home_viewed'
  | 'home_cta_clicked'
  | 'home_featured_clicked'
  | 'home_rail_view_all'
  | 'home_feed_loaded'
  | 'home_feed_item_opened'
  | 'home_feed_item_dismissed'
  | 'home_feed_item_read'
  | 'home_feed_action_completed'
  | 'home_feed_filter_used'
  | 'home_feed_quick_action'
  | 'home_feed_older_loaded'
  | 'club_post_created'
  | 'event_post_created'
  | 'ad_impression'
  | 'ad_click'
  | 'share_card_generated'
  | 'qr_sticker_generated'
  | 'vehicle_favorited'
  | 'vehicle_unfavorited'
  | 'favorites_page_viewed'
  | 'favorite_build_opened'
  | 'favorite_signin_started'
  // Build-page expansion
  | 'achievement_opened'
  | 'build_stats_viewed'
  | 'documentation_score_opened'
  | 'performance_record_created'
  | 'performance_record_opened'
  | 'dyno_record_created'
  | 'dyno_record_opened'
  | 'factory_comparison_opened'
  | 'media_album_opened'
  | 'album_created'
  | 'media_linked_to_modification'
  | 'contributor_requested'
  | 'contributor_opened'
  | 'owner_card_opened'
  | 'share_card_generated'
  | 'qr_sticker_generated'
  | 'global_search_opened'
  | 'global_search_submitted'
  | 'global_search_result_opened'
  | 'discover_advanced_sort_applied'
  | 'build_watch_enabled'
  | 'build_watch_disabled'
  | 'build_update_notification_opened'
  | 'build_explorer_opened'
  | 'build_explorer_category_opened'
  | 'build_explorer_assembly_expanded'
  | 'build_explorer_part_focused'
  | 'build_explorer_detail_opened'
  | 'build_explorer_highlight_used'
  | 'build_explorer_reset'
  | 'build_explorer_search_used'
  | 'car_show_mode_entered'
  | 'car_show_qr_launch'
  | 'car_show_system_explored'
  | 'car_show_assembly_explored'
  | 'car_show_focus_opened'
  | 'car_show_gallery_opened'
  | 'car_show_highlight_viewed'
  | 'car_show_auto_reset'
  | 'car_show_manual_reset'
  | 'car_show_mode_exited'
  // Paid events
  | 'event_registration_started'
  | 'event_registration_submitted'
  | 'registration_approved'
  | 'registration_rejected'
  | 'registration_withdrawn'
  | 'registration_confirmed'
  | 'payment_required_viewed'
  | 'connect_onboarding_started'
  | 'connect_onboarding_completed'
  | 'connect_onboarding_incomplete'
  | 'organizer_payout_ready'
  | 'vehicle_entry_checkout_started'
  | 'vehicle_entry_payment_succeeded'
  | 'vehicle_entry_payment_failed'
  | 'vehicle_entry_refunded'
  | 'vehicle_entry_disputed'
  | 'spectator_checkout_started'
  | 'spectator_ticket_purchased'
  | 'spectator_ticket_scanned'
  | 'spectator_ticket_duplicate_scan'
  | 'vehicle_qr_kit_opened'
  | 'vehicle_qr_png_downloaded'
  | 'vehicle_qr_pdf_downloaded'
  | 'feature_flag_blocked'
  | 'event_entry_mode_set'
  // Bilt Passport
  | 'passport_scan_started'
  | 'passport_scan_completed'
  | 'passport_scan_failed'
  | 'passport_encounter_duplicate'
  | 'passport_encounter_pending'
  | 'passport_encounter_rejected'
  | 'passport_timeline_viewed'
  | 'passport_encounter_opened'
  | 'passport_memory_saved'
  | 'passport_collection_viewed'
  | 'passport_collection_progress_advanced'
  | 'passport_collection_completed'
  | 'passport_campaign_viewed'
  | 'passport_campaign_joined'
  | 'passport_campaign_completed'
  | 'passport_reward_claimed'
  | 'passport_appeal_submitted'
  | 'passport_review_decision_made'
  | 'passport_vehicle_strip_viewed'
  | 'passport_badge_earned'
  | 'passport_recap_viewed'
  | 'passport_owner_analytics_viewed'
  | 'passport_scan_permission_denied'
  | 'passport_scan_manual_fallback'
  | 'passport_offline_sync_completed';

export interface AnalyticsEventPayload {
  vehicle_id?: number;
  event_id?: number;
  metadata?: Record<string, unknown>;
}

export const analyticsMethods = {
  /**
   * Record a proof-metric event. Resolves even on failure (fire-and-forget).
   */
  async trackEvent(
    eventName: AnalyticsEventName,
    payload: AnalyticsEventPayload = {}
  ): Promise<void> {
    try {
      await this.makeRequest('/analytics_track.php', {
        method: 'POST',
        body: JSON.stringify({ event_name: eventName, ...payload }),
      });
    } catch (error) {
      // Analytics must never disrupt the user experience.
      console.debug('analytics trackEvent failed:', eventName, error);
    }
  },
} satisfies ThisType<ApiClient>;
